import { useEffect, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { emailUpdateService } from "@/services/emailUpdateService";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { safeSessionStorage } from "@/utils/safariStorage";

export const PendingEmailBanner = () => {
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);

  const checkStatus = async () => {
    const { pending, newEmail } = await emailUpdateService.checkVerificationStatus();
    
    if (pending && newEmail) {
      setPendingEmail(newEmail);
      safeSessionStorage.setItem('pending_email_change', newEmail);
    } else {
      setPendingEmail(null);
      safeSessionStorage.removeItem('pending_email_change');
    }
  };

  useEffect(() => {
    // Check session storage first for immediate display
    const storedPending = safeSessionStorage.getItem('pending_email_change');
    if (storedPending) {
      setPendingEmail(storedPending);
    }
    
    // Then verify with server immediately
    checkStatus();

    // Set up real-time auth listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Check for email change events
        if (event === 'USER_UPDATED' || event === 'SIGNED_IN') {
          const user = session?.user;
          if (user?.new_email) {
            setPendingEmail(user.new_email);
            safeSessionStorage.setItem('pending_email_change', user.new_email);
          } else {
            setPendingEmail(null);
            safeSessionStorage.removeItem('pending_email_change');
          }
        }
      }
    );

    // Reduced polling interval as backup (5 seconds instead of 30)
    const interval = setInterval(checkStatus, 5000);

    // Page visibility listener - recheck when user returns to tab
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkStatus();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      subscription.unsubscribe();
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  const handleResend = async () => {
    if (!pendingEmail) return;
    setIsResending(true);
    try {
      const result = await emailUpdateService.resendVerificationEmail(pendingEmail);
      if (result.success) {
        toast({
          title: "Verification Email Resent",
          description: `We've sent a new verification email to ${pendingEmail}`
        });
      } else {
        toast({
          title: "Failed to Resend",
          description: result.error || "Please try again later",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to resend verification email",
        variant: "destructive"
      });
    } finally {
      setIsResending(false);
    }
  };
  const handleCancel = async () => {
    setIsCanceling(true);
    try {
      const result = await emailUpdateService.cancelEmailChange();
      if (result.success) {
        toast({
          title: "Email Change Cancelled",
          description: "Your email change request has been cancelled"
        });
        setPendingEmail(null); // Hide banner
      } else {
        toast({
          title: "Failed to Cancel",
          description: result.error || "Please try again later",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to cancel email change",
        variant: "destructive"
      });
    } finally {
      setIsCanceling(false);
    }
  };
  if (!pendingEmail) return null;
  return <Alert className="mb-6 border-amber-500 bg-amber-50 dark:bg-amber-950/20">
      <AlertCircle className="h-4 w-4 text-amber-600" />
      <AlertTitle className="text-amber-900 dark:text-amber-100">
        Email Change Pending Verification
      </AlertTitle>
      <AlertDescription className="text-amber-800 dark:text-amber-200">
        <p className="mb-2">
          We've sent a verification email to{" "}
          <strong className="font-semibold">{pendingEmail}</strong>.
        </p>
        <p className="mb-3">
          Please click the link in that email to complete your email change.
        </p>
        
        <p className="text-xs mb-3 text-amber-700 dark:text-amber-300">
          <strong>Email not received?</strong> Check your spam folder or wait a few minutes. 
          If you've tried multiple times, please wait 5-10 minutes before resending.
        </p>
        
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleResend} disabled={isResending || isCanceling} className="border-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/30 text-slate-50">
            {isResending ? "Sending..." : "Resend Verification Email"}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleCancel} disabled={isResending || isCanceling} className="text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/30">
            {isCanceling ? "Cancelling..." : "Cancel"}
          </Button>
        </div>
      </AlertDescription>
    </Alert>;
};